import { type ComponentProps, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AgentCombobox } from "@/components/Agents/AgentCombobox";
import { useFieldErrors } from "@/components/Tools/use-field-errors";
import { useToast } from "@/hooks/use-toast";
import {
  type PhoneNumber,
  useConfigureTwilioWebhook,
  useConnectSipNumber,
  useImportTwilioNumber,
} from "@/hooks/use-phone-numbers";
import type { SipTransport } from "@/services/phone-number.service";
import {
  type PhoneNumberField,
  type Refusal,
  refusalOf,
  refusedFields,
  sipNumberErrors,
  toE164,
  twilioNumberErrors,
} from "@/utils/phone-number-rules";

interface AddPhoneNumberDialogProps {
  open: boolean;
  onClose: () => void;
  workspaceId: string;
}

type Provider = "twilio" | "sip";

/** Both tabs share the number, its nickname and its agents; each has its own carrier fields. */
const EMPTY = {
  phoneNumber: "",
  nickname: "",
  inboundAgentId: "",
  outboundAgentId: "",
  twilioSid: "",
  authToken: "",
  terminationUri: "",
  transport: "tcp" as SipTransport,
  sipUsername: "",
  sipPassword: "",
};
type Form = typeof EMPTY;
type TextField = Exclude<keyof Form, "transport" | "inboundAgentId" | "outboundAgentId">;

export default function AddPhoneNumberDialog({ open, onClose, workspaceId }: AddPhoneNumberDialogProps) {
  const { toast } = useToast();
  const importTwilioNumber = useImportTwilioNumber();
  const connectSipNumber = useConnectSipNumber();
  const configureWebhook = useConfigureTwilioWebhook();

  const [provider, setProvider] = useState<Provider>("twilio");
  const [form, setForm] = useState<Form>(EMPTY);
  const [busy, setBusy] = useState<"adding" | "configuring" | null>(null);
  const [refusal, setRefusal] = useState<Refusal>();
  const contentRef = useRef<HTMLDivElement>(null);

  const ruleErrors = provider === "twilio" ? twilioNumberErrors(form) : sipNumberErrors(form);
  const errors = { ...ruleErrors, ...refusedFields(refusal, form) };
  const { field, error, check, reset } = useFieldErrors<PhoneNumberField>(errors);

  const set = <K extends keyof Form>(key: K, value: Form[K]) => setForm((current) => ({ ...current, [key]: value }));

  const close = () => {
    setProvider("twilio");
    setForm(EMPTY);
    setRefusal(undefined);
    reset();
    onClose();
  };

  const switchProvider = (next: string) => {
    setProvider(next as Provider);
    // What was shown for the other tab's fields does not carry over.
    setRefusal(undefined);
    reset();
  };

  /** The number exists whatever Twilio says, so a failure here warns and the dialog still closes. */
  const configureTwilio = async (number: PhoneNumber) => {
    setBusy("configuring");
    try {
      await configureWebhook.mutateAsync(number.id);
      toast({
        title: "Phone number added",
        description: `${number.phone_number} is connected and its Twilio voice webhook points at Verbeo.`,
      });
    } catch (failure) {
      const reason = refusalOf(failure, {}, "Twilio could not be reached.").message.replace(/\.?$/, ".");
      toast({
        variant: "destructive",
        title: "Number added, but its Twilio voice webhook is not set up",
        description: `${reason} Fix it, then press "Configure Twilio Voice Webhook" on the number's card.`,
      });
    }
  };

  const handleSubmit = async () => {
    // A refusal from an earlier try no longer applies to what is on screen now.
    setRefusal(undefined);
    // Nothing the server would refuse is sent: the reasons show under their fields.
    if (!check(contentRef.current, ruleErrors)) return;
    setBusy("adding");
    const number = {
      workspace_id: workspaceId,
      phone_number: toE164(form.phoneNumber)!,
      nickname: form.nickname.trim() || null,
      inbound_agent_id: form.inboundAgentId || null,
      outbound_agent_id: form.outboundAgentId || null,
    };
    try {
      if (provider === "twilio") {
        await configureTwilio(
          await importTwilioNumber.mutateAsync({
            ...number,
            twilio_sid: form.twilioSid.trim(),
            twilio_auth_token: form.authToken.trim(),
          }),
        );
      } else {
        const connected = await connectSipNumber.mutateAsync({
          ...number,
          termination_uri: form.terminationUri.trim(),
          transport: form.transport,
          sip_username: form.sipUsername.trim() || null,
          sip_password: form.sipPassword || null,
        });
        toast({
          title: "Phone number connected",
          description: `${connected.phone_number} is connected over LiveKit SIP trunking.`,
        });
      }
      close();
    } catch (failure) {
      setRefusal(refusalOf(failure, form, "The phone number could not be added."));
    } finally {
      setBusy(null);
    }
  };

  // An add in flight finishes in view: closing now would hide whether it worked.
  const handleClose = () => {
    if (!busy) close();
  };

  const input = (name: TextField, id: string, props: ComponentProps<typeof Input> = {}) => (
    <Input
      id={id}
      data-testid={id}
      value={form[name]}
      onChange={(event) => set(name, event.target.value)}
      disabled={!!busy}
      {...field(name, id)}
      {...props}
    />
  );

  const agentPicker = (direction: "inbound" | "outbound", label: string) => {
    const name = `${direction}AgentId` as const;
    const id = `phone-${direction}-agent`;
    return (
      <div className="grid gap-2">
        <Label htmlFor={id}>{label}</Label>
        <AgentCombobox
          id={id}
          workspaceId={workspaceId}
          value={form[name] || null}
          onChange={(agentId) => set(name, agentId ?? "")}
          placeholder={`Select agent for ${direction} calls`}
          clearLabel="No agent assigned"
          disabled={!!busy}
        />
        {error(name, id)}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        ref={contentRef}
        className="sm:max-w-[560px] max-h-[90vh] flex flex-col [&_[aria-invalid=true]]:border-destructive"
      >
        <DialogHeader>
          <DialogTitle>Add Phone Number</DialogTitle>
          <DialogDescription>
            Import an existing Twilio number or connect any SIP-capable carrier via trunking.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={provider} onValueChange={switchProvider} className="mt-4 flex-1 flex flex-col overflow-hidden">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="twilio" disabled={!!busy} data-testid="phone-provider-twilio">
              Import Twilio
            </TabsTrigger>
            <TabsTrigger value="sip" disabled={!!busy} data-testid="phone-provider-sip">
              SIP Trunking
            </TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-y-auto pr-1 mt-4 grid gap-4 content-start">
            <div className="grid gap-2">
              <Label htmlFor="phone-number">Phone Number *</Label>
              {input("phoneNumber", "phone-number", { placeholder: "+14155550123" })}
              {error("phoneNumber", "phone-number")}
              <p className="text-xs text-muted-foreground">E.164 format: + and the country code.</p>
            </div>

            <TabsContent value="twilio" className="mt-0 grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="phone-twilio-sid">Twilio Account SID *</Label>
                {input("twilioSid", "phone-twilio-sid", { placeholder: "AC…" })}
                {error("twilioSid", "phone-twilio-sid")}
                <p className="text-xs text-muted-foreground">
                  Find this in your Twilio Console under Account Info (starts with AC)
                </p>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="phone-auth-token">Twilio Auth Token *</Label>
                {input("authToken", "phone-auth-token", {
                  type: "password",
                  placeholder: "Enter your Twilio Auth Token",
                })}
                {error("authToken", "phone-auth-token")}
                <p className="text-xs text-muted-foreground">Find this in your Twilio Console under Account Info</p>
              </div>
            </TabsContent>

            <TabsContent value="sip" className="mt-0 grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="phone-termination-uri">Termination URI *</Label>
                {input("terminationUri", "phone-termination-uri", { placeholder: "acme.pstn.twilio.com" })}
                {error("terminationUri", "phone-termination-uri")}
                <p className="text-xs text-muted-foreground">Your carrier's SIP termination hostname.</p>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="phone-transport">Outbound Transport</Label>
                <Select
                  value={form.transport}
                  onValueChange={(value) => set("transport", value as SipTransport)}
                  disabled={!!busy}
                >
                  <SelectTrigger id="phone-transport" data-testid="phone-transport">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tcp">TCP</SelectItem>
                    <SelectItem value="udp">UDP</SelectItem>
                    <SelectItem value="tls">TLS</SelectItem>
                    <SelectItem value="auto">Auto</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="phone-sip-username">
                  SIP Trunk User Name <span className="text-muted-foreground">(Optional)</span>
                </Label>
                {input("sipUsername", "phone-sip-username")}
                {error("sipUsername", "phone-sip-username")}
              </div>
              <div className="grid gap-2">
                <Label htmlFor="phone-sip-password">
                  SIP Trunk Password <span className="text-muted-foreground">(Optional)</span>
                </Label>
                {input("sipPassword", "phone-sip-password", { type: "password" })}
                {error("sipPassword", "phone-sip-password")}
              </div>
            </TabsContent>

            <div className="grid gap-2">
              <Label htmlFor="phone-nickname">
                Nickname <span className="text-muted-foreground">(Optional)</span>
              </Label>
              {input("nickname", "phone-nickname", { placeholder: "Main line" })}
              {error("nickname", "phone-nickname")}
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Agent Assignments</h4>
              <div className="grid gap-4">
                {agentPicker("inbound", "Inbound Calls Agent")}
                {agentPicker("outbound", "Outbound Calls Agent")}
              </div>
            </div>
          </div>
        </Tabs>

        {refusal && !refusal.field && (
          <p role="alert" data-testid="phone-save-error" className="text-sm text-destructive">
            {refusal.message}
          </p>
        )}

        <DialogFooter className="flex-shrink-0 mt-4">
          <Button variant="outline" onClick={handleClose} disabled={!!busy} data-testid="phone-cancel">
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!!busy} aria-busy={!!busy} data-testid="phone-submit">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {busy === "adding" ? "Adding…" : busy === "configuring" ? "Configuring…" : "Add Phone Number"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
